import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IconLock, IconCheck, IconArrowLeft, IconEye, IconEyeOff } from "@tabler/icons-react";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import { AuthShell } from "@/pages/auth/Login";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function strengthOf(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const strength = strengthOf(pw);
  const strengthLabel = ["", "débil", "aceptable", "buena", "fuerte"][strength] || "";
  const strengthColor = ["", "bg-errtext", "bg-warntext", "bg-teal", "bg-teal-deep"][strength] || "bg-line";
  const mismatch = pw && pw2 && pw !== pw2;
  const tooShort = pw && pw.length < 8;
  const canSubmit = !!token && pw.length >= 8 && pw === pw2 && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true); setError("");
    try {
      await axios.post(`${BACKEND}/api/auth/password/reset`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : (lang === "es" ? "No se pudo actualizar la contraseña." : "Could not update password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <p className="eyebrow">Chiessa Institute</p>
      <h1 className="font-display font-medium text-[30px] mt-2 leading-tight">
        {lang === "es" ? "Nueva contraseña" : "New password"}
      </h1>
      {done ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 card border-l-[3px] border-l-teal bg-teal-soft/40"
          data-testid="reset-success"
        >
          <div className="flex items-start gap-2 text-teal-deep">
            <IconCheck size={16} className="mt-0.5" />
            <div>
              <p className="text-[14px] font-medium">
                {lang === "es" ? "¡Contraseña actualizada!" : "Password updated!"}
              </p>
              <p className="text-[12.5px] text-muted mt-2">
                {lang === "es" ? "Te llevamos a iniciar sesión…" : "Redirecting to sign in…"}
              </p>
            </div>
          </div>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
          <p className="text-muted text-[13.5px]">
            {lang === "es"
              ? "Elegí una contraseña que puedas recordar. Este enlace sirve solo una vez."
              : "Choose a memorable password. This link works only once."}
          </p>

          <div className="field">
            <label>{lang === "es" ? "Nueva contraseña" : "New password"}</label>
            <div className="relative">
              <IconLock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-tert" />
              <input
                className="input pl-8 pr-9"
                type={show ? "text" : "password"}
                required
                minLength={8}
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-pw"
              />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-tert" onClick={() => setShow((s) => !s)} tabIndex={-1}>
                {show ? <IconEyeOff size={14} /> : <IconEye size={14} />}
              </button>
            </div>
            {pw && (
              <div className="mt-2">
                <div className="w-full h-1 bg-line rounded overflow-hidden">
                  <div className={`h-full transition-all ${strengthColor}`} style={{ width: `${(strength / 4) * 100}%` }} />
                </div>
                <p className="text-[11.5px] text-tert mt-1">
                  {lang === "es" ? "Fortaleza" : "Strength"}: <span className="capitalize">{strengthLabel}</span>
                </p>
              </div>
            )}
            {tooShort && (
              <p className="text-[11.5px] text-errtext mt-1">
                {lang === "es" ? "Mínimo 8 caracteres." : "At least 8 characters."}
              </p>
            )}
          </div>

          <div className="field">
            <label>{lang === "es" ? "Repetir contraseña" : "Repeat password"}</label>
            <div className="relative">
              <IconLock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-tert" />
              <input
                className="input pl-8"
                type={show ? "text" : "password"}
                required
                minLength={8}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-pw2"
              />
            </div>
            {mismatch && (
              <p className="text-[11.5px] text-errtext mt-1" data-testid="reset-mismatch">
                {lang === "es" ? "Las contraseñas no coinciden." : "Passwords do not match."}
              </p>
            )}
          </div>

          {error && <p className="text-errtext text-[13px] bg-errbg rounded-md px-3 py-2" data-testid="reset-error">{error}</p>}

          <button type="submit" className="btn w-full" disabled={!canSubmit} data-testid="reset-submit">
            {loading ? (lang === "es" ? "Actualizando..." : "Updating...") : (lang === "es" ? "Cambiar contraseña" : "Change password")}
          </button>

          <Link to="/login" className="block text-center text-[12.5px] text-muted mt-2" data-testid="reset-back-link">
            <IconArrowLeft size={11} className="inline mr-1" />
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
